import { isBefore, parseISO } from 'date-fns';
import { SubscriptionResponseDto, SubscriptionStatus } from '../core/api';

export function isSubscriptionCanceled(subscription: SubscriptionResponseDto | null | undefined): boolean {
  if (!subscription) {
    return false;
  }
  return (
    subscription.status === SubscriptionStatus.Canceled ||
    subscription.status === SubscriptionStatus.Suspended ||
    (subscription.canceledAt != null && subscription.canceledAt !== '')
  );
}

/**
 * Mirrors the backend `SubscriptionAccessService` matrix so the UI reaches the same verdict as the API.
 *
 * The backend is the enforcing instance; this only decides whether to route to the lock screen. It stays
 * deliberately fail-open on missing or unparseable data so nobody is locked out of the UI by bad data —
 * a request that actually needs access will still be refused with 402.
 *
 * @param subscription The subscription, or null when none was loaded.
 * @returns true when the subscription no longer grants access.
 */
export function isSubscriptionExpired(subscription: SubscriptionResponseDto | null | undefined): boolean {
  if (!subscription) {
    return false;
  }

  switch (subscription.status) {
    case SubscriptionStatus.Trial:
      // A trial without an end date cannot be judged here; the backend fails closed on it.
      return subscription.trialEnd ? hasPassed(subscription.trialEnd) : false;

    case SubscriptionStatus.Active:
      return false;

    case SubscriptionStatus.Canceled:
      // Cancelled but paid until the end of the period: access runs out with the period.
      return hasPassed(subscription.endDate ?? subscription.currentPeriodEnd);

    case SubscriptionStatus.PastDue:
    case SubscriptionStatus.Suspended:
    case SubscriptionStatus.Incomplete:
    case SubscriptionStatus.Paused:
      return true;

    default:
      return false;
  }
}

function hasPassed(date: string | null | undefined): boolean {
  if (!date) {
    return false;
  }

  try {
    return isBefore(parseISO(date), new Date());
  } catch {
    return false;
  }
}
